import { createContext, useContext, type ReactNode } from "react";
import type {
  FontFaceDescriptor,
  Project,
  DamProviderInfo,
  PlacedPdf,
} from "@proof/core";
import type {
  FontLoader,
  FormGeneratorProps,
  ImageTransformer,
  AssetLoader,
} from "@proof/ui";
export interface EditorPresentation {
  /** Presentation controls only; server authorization remains authoritative. */
  enableStatements?: boolean;
  enableRules?: boolean;
  enableFormatting?: boolean;
  enableValidation?: boolean;
  enableDynamicSources?: boolean;
  enableConditionalVisibility?: boolean;
  enablePartOverrides?: boolean;
  allowProjectRename?: boolean;
  showHeading?: boolean;
  showDescription?: boolean;
}
export interface AuthoringServices {
  /** The host owns impersonation, popup authorization and audit attribution. */
  tryItOut?: (project: Project, signal: AbortSignal) => void | Promise<void>;
  editorPresentation?: EditorPresentation;
  listFormProjects?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<{ id: string; name: string }[]>;
  loadFormProject?: (
    id: string,
    signal: AbortSignal,
  ) => Promise<Project | undefined>;
  listFormProfiles?: (
    query: string,
    signal: AbortSignal,
    project?: Project,
  ) => Promise<
    { id: string; name: string; values: import("@proof/core").Values }[]
  >;
  listPartOverrides?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<{ id: string; name: string; sku?: string }[]>;
  loadPartOverride?: (
    id: string,
    signal: AbortSignal,
  ) => Promise<import("@proof/core").Part>;
  searchPartSkus?: (
    query: string,
    signal: AbortSignal,
  ) => Promise<{ id: string; name: string; sku?: string }[]>;

  listFieldPresets?: (
    query?: import("@proof/core").FieldPresetQuery,
  ) => Promise<import("@proof/core").FieldPresetCatalog>;
  saveFieldPreset?: (
    input: import("@proof/core").FieldPresetWrite,
  ) => Promise<import("@proof/core").FieldPreset>;
  profileDataset?: (
    resource: import("@proof/core").Resource,
  ) => Promise<import("@proof/core").ProfileDatasetView>;
  saveProfileDataset?: (
    input: import("@proof/core").ProfileDatasetWrite,
    resource: import("@proof/core").Resource,
  ) => Promise<import("@proof/core").ProfileDatasetView>;
  listResources?: () => Promise<import("@proof/core").Resource[]>;
  saveResource?: (
    resource: import("@proof/core").Resource,
  ) => Promise<import("@proof/core").Resource>;
  removeBackground?: FormGeneratorProps["removeBackground"];
  dam?: (
    project: Project,
    partId?: string,
  ) => NonNullable<FormGeneratorProps["dam"]>;
  listDamProviders?: () => Promise<DamProviderInfo[]>;
  loadAsset?: AssetLoader;
  importBase?: (file: File, signal: AbortSignal) => Promise<Project>;
  importPdfArtwork?: (
    file: File,
    page: number,
    signal: AbortSignal,
  ) => Promise<PlacedPdf>;
  transformImage?: ImageTransformer;
  maps?: FormGeneratorProps["maps"];
  listFonts?: () => Promise<FontFaceDescriptor[]>;
  loadFont?: FontLoader;
  assetUrl?: (id: string) => string;
  upload?: FormGeneratorProps["upload"];
  assets?: FormGeneratorProps["assets"];
  listAssets?: (
    signal?: AbortSignal,
  ) => Promise<NonNullable<FormGeneratorProps["assets"]>>;
  onProofStatus?: (status: { ready: boolean; error?: string }) => void;
}
const defaults: AuthoringServices = {
  loadFont: async () => {
    throw new Error("Provide a font loader for managed font faces");
  },
  assetUrl: () =>
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
};
const Services = createContext(defaults);
export function AuthoringServicesProvider({
  services,
  children,
}: {
  services?: AuthoringServices;
  children: ReactNode;
}) {
  const inherited = useContext(Services);
  return (
    <Services.Provider
      value={{
        ...inherited,
        ...Object.fromEntries(
          Object.entries(services || {}).filter(
            ([, value]) => value !== undefined,
          ),
        ),
      }}
    >
      {children}
    </Services.Provider>
  );
}
export const useAuthoringServices = () => useContext(Services);
